import React from 'react';
import CoverageGroup from './CoverageGroup';
import type { RaterXPreset } from '../../../../api/types/rater-x-preset';
import { useUnderwriterContext } from '../../provider/UnderwriterProvider';
import { useGetFormsByCoverages } from '../../../../hooks/useGetFormsByCoverages';
import { isAmendatoryCategory } from '../../../../../../primeX-underwriter/constants';

const UnderwriterCoverageListing = ({ categories }: CoverageListingProps) => {
  const context = useUnderwriterContext();

  // FIR-21152: endorsements carry a form number and previewable wording. Only the
  // amendatory category has forms registered, so the fetch is scoped to its keys —
  // the hook disables itself when the list is empty.
  //
  // `coverage.key` is the SCREAMING_SNAKE coverage key; `coverage.field.key` is the
  // camelCase field key. pdf_catalog.doc_name holds the FIELD key, and the lookup is an
  // exact match, so sending coverage keys returns an empty map for every endorsement.
  const amendatoryFieldKeys = React.useMemo(() => {
    return (categories ?? [])
      .filter((category) => isAmendatoryCategory(category.key))
      .flatMap((category) =>
        category.groups.flatMap((group) =>
          group.coverages.map((coverage) => coverage.field?.key)
        )
      )
      .filter((fieldKey): fieldKey is string => Boolean(fieldKey));
  }, [categories]);

  // Quote-time only: the mid-term-endorsement flow carries a policy rather than an account,
  // so no state resolves, the hook disables itself, and MTE shows no form affordance. That is
  // this story's agreed scope, not an oversight -- widening it needs the MTE risk state.
  const quoteAccountState =
    'account' in context.state ? context.state.account?.state : undefined;

  const coverageForms = useGetFormsByCoverages(
    amendatoryFieldKeys,
    context.state.productType,
    quoteAccountState ?? ''
  );

  if (!categories) {
    return null;
  }

  return (
    <>
      {categories.map((category) => (
        <React.Fragment key={category.title}>
          <CoverageGroup
            {...category}
            categoryKey={category.key}
            coverageForms={
              isAmendatoryCategory(category.key)
                ? coverageForms.data?.coverageForms
                : undefined
            }
            isCoverageFormsLoading={
              isAmendatoryCategory(category.key) && coverageForms.isFetching
            }
          />
        </React.Fragment>
      ))}
    </>
  );
};

export default UnderwriterCoverageListing;

type CoverageListingProps = {
  readonly categories: RaterXPreset['coverageCategories'] | undefined;
};
